import {
  fetchQuestionBasket,
  fetchQuestionBasketMeta,
  addQuestionBasketItem,
  removeQuestionBasketItem,
  clearQuestionBasket,
  clearQuestionBasketSession,
  setQuestionBasketContext,
  reorderQuestionBasket,
  createPaperFromQuestionBasket
} from '@/utils/api';

const BASKET_CONTEXT_META_KEY = 'questionBasketContextMeta';

function loadContextMeta() {
  try {
    const raw = sessionStorage.getItem(BASKET_CONTEXT_META_KEY);
    if (!raw) return { contextType: 'paper', contextExamId: null };
    const parsed = JSON.parse(raw);
    return {
      contextType: parsed && parsed.contextType === 'exam' ? 'exam' : 'paper',
      contextExamId: parsed && parsed.contextExamId != null ? Number(parsed.contextExamId) : null
    };
  } catch (e) {
    return { contextType: 'paper', contextExamId: null };
  }
}

function persistContextMeta(meta) {
  try {
    sessionStorage.setItem(BASKET_CONTEXT_META_KEY, JSON.stringify(meta || {}));
  } catch (e) {
    // ignore
  }
}

const initialContextMeta = loadContextMeta();

const state = {
  items: [],
  maxSize: 50,
  loading: false,
  targetPaperId: null,
  targetPaperName: null,
  contextType: initialContextMeta.contextType,
  contextExamId: initialContextMeta.contextExamId
};

const mutations = {
  SET_ITEMS(state, items) {
    state.items = items || [];
  },
  SET_MAX(state, n) {
    state.maxSize = n || 50;
  },
  SET_LOADING(state, v) {
    state.loading = v;
  },
  SET_CONTEXT(state, { targetPaperId, targetPaperName, contextType, contextExamId }) {
    state.targetPaperId = targetPaperId != null ? targetPaperId : null;
    state.targetPaperName = targetPaperName != null ? targetPaperName : null;
    state.contextType = contextType === 'exam' ? 'exam' : 'paper';
    state.contextExamId = state.contextType === 'exam' && contextExamId != null ? Number(contextExamId) : null;
    persistContextMeta({
      contextType: state.contextType,
      contextExamId: state.contextExamId
    });
  }
};

const actions = {
  async refreshMeta({ commit, state }) {
    const meta = await fetchQuestionBasketMeta();
    if (meta && meta.code === 0 && meta.data) {
      if (meta.data.maxSize != null) {
        commit('SET_MAX', meta.data.maxSize);
      }
      commit('SET_CONTEXT', {
        targetPaperId: meta.data.targetPaperId,
        targetPaperName: meta.data.targetPaperName,
        contextType: state.contextType,
        contextExamId: state.contextExamId
      });
    }
  },
  async initBasket({ dispatch }) {
    await dispatch('refreshMeta');
    await dispatch('loadBasket');
  },
  async loadBasket({ commit }) {
    commit('SET_LOADING', true);
    try {
      const res = await fetchQuestionBasket();
      if (res && res.code === 0) {
        commit('SET_ITEMS', res.data || []);
      }
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async addToBasket({ dispatch }, questionId) {
    const res = await addQuestionBasketItem(questionId);
    if (res && res.code === 0) {
      await dispatch('loadBasket');
      return true;
    }
    return false;
  },
  async removeFromBasket({ dispatch }, questionId) {
    await removeQuestionBasketItem(questionId);
    await dispatch('loadBasket');
  },
  /** 仅清空题目，保留关联试卷会话 */
  async clearBasketItems({ dispatch }) {
    await clearQuestionBasket();
    await dispatch('loadBasket');
  },
  /** 清空题目并清除关联试卷 */
  async clearBasketFull({ commit, dispatch }) {
    await clearQuestionBasket();
    await clearQuestionBasketSession();
    commit('SET_CONTEXT', { targetPaperId: null, targetPaperName: null, contextType: 'paper', contextExamId: null });
    await dispatch('loadBasket');
  },
  async reorderBasket({ dispatch }, questionIds) {
    await reorderQuestionBasket(questionIds);
    await dispatch('loadBasket');
  },
  async setBasketContext({ commit, dispatch }, payload) {
    await setQuestionBasketContext({
      targetPaperId: payload.targetPaperId,
      targetPaperName: payload.targetPaperName
    });
    commit('SET_CONTEXT', {
      targetPaperId: payload.targetPaperId,
      targetPaperName: payload.targetPaperName,
      contextType: payload.contextType || 'paper',
      contextExamId: payload.contextExamId != null ? payload.contextExamId : null
    });
    await dispatch('refreshMeta');
  },
  async createPaperFromBasket({ dispatch }, payload) {
    const res = await createPaperFromQuestionBasket(payload || {});
    if (res && res.code === 0 && res.data != null) {
      await dispatch('loadBasket');
      await dispatch('refreshMeta');
      return res.data;
    }
    return null;
  }
};

const getters = {
  basketCount: (s) => (s.items || []).length,
  basketQuestionIds: (s) => (s.items || []).map((x) => x.questionId)
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
